import { FormEvent, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import Card from "../../components/shared/Card/Card";
import LoadingWheel from "../../components/shared/LoadingWheel";
import useNotification from "../../components/notifications/useNotifications";
import { usePageNameContext } from "../../contexts/PageTitleContext";
import { useAuth } from "../../contexts/AuthContext";
import useTickets from "../../data/useTickets";
import useApiHelper from "../../hooks/useApiHelper";
import { useUserLookup } from "../../hooks/useUserLookup";
import { TicketPriority, TicketStatus } from "../../models/Ticket";

const statusText: Record<number, string> = {
  [TicketStatus.Open]: "Open",
  [TicketStatus.InProgress]: "In Progress",
  [TicketStatus.Resolved]: "Resolved",
  [TicketStatus.Closed]: "Closed",
};

const priorityText: Record<number, string> = {
  [TicketPriority.Low]: "Low",
  [TicketPriority.Medium]: "Medium",
  [TicketPriority.High]: "High",
  [TicketPriority.Urgent]: "Urgent",
};

const statusStyles: Record<number, string> = {
  [TicketStatus.Open]: "bg-green-50 text-green-700 ring-green-600/20",
  [TicketStatus.InProgress]: "bg-amber-50 text-amber-700 ring-amber-600/20",
  [TicketStatus.Resolved]: "bg-blue-50 text-blue-700 ring-blue-600/20",
  [TicketStatus.Closed]: "bg-slate-100 text-slate-700 ring-slate-600/20",
};

const priorityStyles: Record<number, string> = {
  [TicketPriority.Low]: "bg-slate-100 text-slate-700 ring-slate-600/20",
  [TicketPriority.Medium]: "bg-amber-50 text-amber-700 ring-amber-600/20",
  [TicketPriority.High]: "bg-orange-50 text-orange-700 ring-orange-600/20",
  [TicketPriority.Urgent]: "bg-red-50 text-red-700 ring-red-600/20",
};

export default function ViewTicket() {
  const navigate = useNavigate();
  const { ticketId } = useParams();
  const { setInfo } = usePageNameContext();
  const { userId } = useAuth();
  const { addNotification } = useNotification();
  const { ticket, addComment, uploadAttachment } = useTickets(ticketId);
  const { getFile } = useApiHelper();
  const { users, findUser } = useUserLookup();

  const replyInputRef = useRef<HTMLTextAreaElement>(null);
  const [comment, setComment] = useState("");
  const [filesToUpload, setFilesToUpload] = useState<File[]>([]);
  const [activeImagePreview, setActiveImagePreview] = useState<{ src: string; name: string } | null>(
    null
  );
  const [imagePreviewUrls, setImagePreviewUrls] = useState<Record<string, string>>({});
  const maxAttachmentSizeBytes = 100 * 1024 * 1024;

  const ticketData = ticket.data?.data;
  const comments = useMemo(
    () =>
      [...(ticketData?.notes ?? [])].sort(
        (a, b) => new Date(a.created).getTime() - new Date(b.created).getTime()
      ),
    [ticketData?.notes]
  );
  const attachments = useMemo(
    () =>
      [...(ticketData?.attachments ?? [])].sort(
        (a, b) => new Date(a.created).getTime() - new Date(b.created).getTime()
      ),
    [ticketData?.attachments]
  );
  const imageAttachments = useMemo(
    () => attachments.filter((attachment) => (attachment.contentType || "").toLowerCase().startsWith("image/")),
    [attachments]
  );
  const fileAttachments = useMemo(
    () => attachments.filter((attachment) => !(attachment.contentType || "").toLowerCase().startsWith("image/")),
    [attachments]
  );

  const createdBy = useMemo(() => {
    if (!ticketData?.createdByUserId) return "Unknown";
    const user = findUser(ticketData.createdByUserId);
    if (!user) return "Unknown";
    const fullName = `${user.forename ?? ""} ${user.surname ?? ""}`.trim();
    return fullName || user.email || user.username || "Unknown";
  }, [findUser, ticketData?.createdByUserId]);

  const unreadStartIndex = useMemo(() => {
    if (!userId || !ticketData || comments.length === 0) return -1;
    if (ticketData.createdByUserId.toLowerCase() !== userId.toLowerCase()) return -1;

    const latest = comments[comments.length - 1];
    if (!latest?.isFromStaff) return -1;

    let lastCustomerIndex = -1;
    comments.forEach((message, index) => {
      if (!message.isFromStaff) lastCustomerIndex = index;
    });

    return Math.min(lastCustomerIndex + 1, comments.length - 1);
  }, [comments, ticketData, userId]);

  const isUnread = unreadStartIndex >= 0;
  const waitingOn = useMemo(() => {
    const latest = comments[comments.length - 1];
    return latest?.isFromStaff ? "Waiting on customer" : "Waiting on support";
  }, [comments]);
  const lastReplyBy = useMemo(() => {
    const latest = comments[comments.length - 1];
    if (!latest) return "No replies yet";
    return latest.authorName || latest.authorEmail || (latest.isFromStaff ? "Support" : "Customer");
  }, [comments]);

  const getInitials = (name?: string) => {
    const clean = (name || "").trim();
    if (!clean) return "UN";
    const parts = clean.split(/\s+/).filter(Boolean);
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return `${parts[0][0] ?? ""}${parts[1][0] ?? ""}`.toUpperCase();
  };

  const formatFileSize = (size: number) => {
    if (size < 1024) return `${size} B`;
    if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
    return `${(size / (1024 * 1024)).toFixed(2)} MB`;
  };

  useEffect(() => {
    const urlsToRevoke: string[] = [];
    let isDisposed = false;

    const loadPreviews = async () => {
      if (!ticketData?.id || imageAttachments.length === 0) {
        setImagePreviewUrls({});
        return;
      }

      const nextPreviews: Record<string, string> = {};
      for (const attachment of imageAttachments) {
        try {
          const blob = await getFile(`/ticket/${ticketData.id}/attachments/${attachment.id}/view`);
          if (isDisposed) return;
          const objectUrl = URL.createObjectURL(blob);
          urlsToRevoke.push(objectUrl);
          nextPreviews[attachment.id] = objectUrl;
        } catch {
          // ignore preview fetch failures per file
        }
      }

      if (!isDisposed) setImagePreviewUrls(nextPreviews);
    };

    loadPreviews();

    return () => {
      isDisposed = true;
      urlsToRevoke.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [ticketData?.id, imageAttachments]);

  useEffect(() => {
    setInfo({
      name: "Ticket Conversation",
      desc: "Review and reply to this support request",
    });
  }, [setInfo]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (
        event.key.toLowerCase() === "r" &&
        !event.metaKey &&
        !event.ctrlKey &&
        !event.altKey
      ) {
        const tagName = (event.target as HTMLElement | null)?.tagName?.toLowerCase();
        if (tagName === "input" || tagName === "textarea") return;
        event.preventDefault();
        replyInputRef.current?.focus();
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  const submitComment = (e: FormEvent) => {
    e.preventDefault();
    if (!ticketId || !ticketData || !comment.trim()) return;

    addComment.mutate(
      {
        ticketId,
        request: {
          note: comment.trim(),
          isInternal: false,
        },
      },
      {
        onSuccess: () => {
          setComment("");
          addNotification({
            variant: "success",
            primaryText: "Reply sent",
          });
        },
        onError: (error: any) => {
          addNotification({
            variant: "error",
            primaryText: error?.message || "Failed to send message",
          });
        },
      }
    );
  };

  const submitAttachments = async (e: FormEvent) => {
    e.preventDefault();
    if (!ticketId || filesToUpload.length === 0) return;

    const oversizedFile = filesToUpload.find((file) => file.size > maxAttachmentSizeBytes);
    if (oversizedFile) {
      addNotification({
        variant: "warn",
        primaryText: `${oversizedFile.name} exceeds the 100MB limit`,
      });
      return;
    }

    try {
      for (const file of filesToUpload) {
        const formData = new FormData();
        formData.append("file", file);
        await uploadAttachment.mutateAsync({ ticketId, data: formData });
      }
      setFilesToUpload([]);
      addNotification({
        variant: "success",
        primaryText: "Attachment(s) uploaded",
      });
    } catch (error: any) {
      addNotification({
        variant: "error",
        primaryText: error?.message || "Failed to upload attachment(s)",
      });
    }
  };

  const downloadAttachment = async (attachmentId: string, fileName: string) => {
    if (!ticketData?.id) return;
    try {
      const blob = await getFile(`/ticket/${ticketData.id}/attachments/${attachmentId}/download`);
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = objectUrl;
      link.download = fileName || "attachment";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(objectUrl);
    } catch {
      addNotification({
        variant: "error",
        primaryText: "Failed to download attachment",
      });
    }
  };

  if (ticket.isLoading) {
    return (
      <div className="my-4 mt-8 flex min-h-[50vh] items-center justify-center rounded-lg bg-white p-4 shadow">
        <LoadingWheel />
      </div>
    );
  }

  if (ticket.isError || !ticketData) {
    return <p className="text-lg font-medium text-red-600">An error occurred retrieving this ticket.</p>;
  }

  return (
    <div className="space-y-4 bg-slate-50 pb-4">
      <Card>
        <div className="sticky top-16 z-10 border-b border-slate-200 bg-white p-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="text-xs text-slate-500">Support / Tickets / #{ticketData.id.slice(-6)}</p>
              <div className="mt-1 flex items-center gap-2">
                <h2 className="text-xl font-semibold text-slate-900">{ticketData.subject}</h2>
                {isUnread ? <span className="h-2.5 w-2.5 rounded-full bg-purple-500" /> : null}
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                className="rounded-lg bg-purple-600 px-4 py-2 text-sm font-semibold text-white hover:bg-purple-700"
                onClick={() => replyInputRef.current?.focus()}
              >
                Reply
              </button>
              <button
                type="button"
                className="rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50"
                onClick={() => navigate("/tickets")}
              >
                Back
              </button>
            </div>
          </div>
        </div>
      </Card>

      <div className="grid gap-4 lg:grid-cols-10">
        <div className="space-y-4 lg:col-span-7">
          <Card>
            <div className="space-y-2 p-4">
              <h3 className="text-sm font-semibold text-slate-900">Ticket Summary</h3>
              <p className="whitespace-pre-wrap text-sm leading-relaxed text-slate-700">{ticketData.description}</p>
              <p className="text-xs text-slate-500">
                Created by {createdBy} on {new Date(ticketData.created).toLocaleString()}
              </p>
            </div>
          </Card>

          <Card>
            <div className="space-y-3 p-4">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold text-slate-900">Conversation</h3>
                <p className="text-xs text-slate-500">
                  Last reply by {lastReplyBy} - {waitingOn}
                </p>
              </div>

              {comments.length === 0 ? (
                <p className="text-sm text-gray-500">No replies yet.</p>
              ) : (
                <div className="space-y-3">
                  {comments.map((item, index) => {
                    const isStaffMessage = item.isFromStaff === true;
                    const displayName = isStaffMessage
                      ? "Support"
                      : item.authorName || item.authorEmail || "Customer";

                    return (
                      <div key={item.id} className={`${isStaffMessage ? "pr-10" : "pl-10"}`}>
                        {index === unreadStartIndex ? (
                          <div className="mb-2 flex items-center gap-2">
                            <div className="h-px flex-1 bg-purple-200" />
                            <span className="rounded-full bg-purple-50 px-2 py-0.5 text-[11px] font-semibold text-purple-700">
                              New messages
                            </span>
                            <div className="h-px flex-1 bg-purple-200" />
                          </div>
                        ) : null}

                        <div className={`flex ${isStaffMessage ? "justify-start" : "justify-end"}`}>
                          <div
                            id={`note-${item.id}`}
                            className={`group w-full max-w-2xl rounded-2xl border p-3 ${
                              item.isInternal
                                ? "border-amber-200 bg-amber-50"
                                : isStaffMessage
                                  ? "border-purple-200 bg-purple-50"
                                  : "border-slate-200 bg-white"
                            }`}
                          >
                          <div className="mb-2 flex items-start justify-between gap-3">
                            <div className="flex items-center gap-2">
                              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-purple-100 text-xs font-semibold text-purple-700">
                                {getInitials(displayName)}
                              </div>
                              <div>
                                <p className="text-sm font-semibold text-slate-800">{displayName}</p>
                              </div>
                            </div>

                            <div className="text-right">
                              <p className="text-xs text-slate-500">{new Date(item.created).toLocaleString()}</p>
                              <div className="mt-1 flex justify-end gap-2 opacity-0 transition group-hover:opacity-100">
                                <button
                                  type="button"
                                  className="text-xs text-slate-500 hover:text-slate-700"
                                  onClick={async () => {
                                    try {
                                      await navigator.clipboard.writeText(
                                        `${window.location.origin}/tickets/${ticketData.id}#note-${item.id}`
                                      );
                                      addNotification({
                                        variant: "success",
                                        primaryText: "Message link copied",
                                      });
                                    } catch {
                                      addNotification({
                                        variant: "error",
                                        primaryText: "Could not copy message link",
                                      });
                                    }
                                  }}
                                >
                                  Copy link
                                </button>
                                <button
                                  type="button"
                                  className="text-xs text-slate-500 hover:text-slate-700"
                                  onClick={() => {
                                    setComment((prev) =>
                                      `> ${item.note.replace(/\n/g, "\n> ")}\n\n${prev}`.trimStart()
                                    );
                                    replyInputRef.current?.focus();
                                  }}
                                >
                                  Quote
                                </button>
                              </div>
                            </div>
                          </div>
                          <p className="whitespace-pre-wrap text-sm leading-relaxed text-slate-700">{item.note}</p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </Card>

          <Card>
            <form onSubmit={submitComment} className="sticky bottom-4 space-y-3 rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="flex items-center justify-between gap-3">
                <p className="text-sm font-semibold text-slate-900">Reply</p>
                <span className="text-xs text-slate-500">Press R to focus reply</span>
              </div>

              <textarea
                ref={replyInputRef}
                className="w-full rounded-lg border border-slate-300 p-3 text-sm text-slate-800 focus:border-purple-500 focus:outline-none focus:ring-2 focus:ring-purple-200"
                rows={5}
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder="Write your reply..."
              />

              <div className="flex flex-wrap justify-end gap-2">
                <button
                  type="submit"
                  className="rounded-lg bg-purple-600 px-4 py-2 text-sm font-semibold text-white hover:bg-purple-700 disabled:opacity-60"
                  disabled={!comment.trim() || addComment.isLoading}
                >
                  {addComment.isLoading ? "Sending..." : "Send reply"}
                </button>
              </div>
            </form>
          </Card>
        </div>

        <div className="space-y-4 lg:col-span-3">
          <Card>
            <div className="space-y-3 p-4">
              <h3 className="text-sm font-semibold text-slate-900">Ticket status</h3>
              <div className="flex flex-wrap items-center gap-2">
                <span
                  className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold ring-1 ${
                    statusStyles[ticketData.status]
                  }`}
                >
                  {statusText[ticketData.status]}
                </span>
                <span
                  className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold ring-1 ${
                    priorityStyles[ticketData.priority]
                  }`}
                >
                  {priorityText[ticketData.priority]}
                </span>
              </div>
              <p className="text-xs text-slate-600">
                Updated: {new Date(ticketData.updated || ticketData.created).toLocaleString()}
              </p>
            </div>
          </Card>

          <Card>
            <div className="space-y-3 p-4">
              <h3 className="text-sm font-semibold text-slate-900">People</h3>
              <div className="rounded-lg border border-slate-200 p-3">
                <p className="text-xs text-slate-500">Requester</p>
                <p className="text-sm font-medium text-slate-800">{createdBy}</p>
              </div>
            </div>
          </Card>

          <Card>
            <div className="space-y-3 p-4">
              <h3 className="text-sm font-semibold text-slate-900">Attachments</h3>

              {attachments.length === 0 ? (
                <p className="text-sm text-slate-500">No attachments yet.</p>
              ) : (
                <>
                  {imageAttachments.length > 0 ? (
                    <div className="grid grid-cols-3 gap-2">
                      {imageAttachments.map((attachment) => (
                        <button
                          key={attachment.id}
                          type="button"
                          className="overflow-hidden rounded-lg border border-slate-200 hover:border-purple-300"
                          onClick={() => {
                            const previewUrl = imagePreviewUrls[attachment.id];
                            if (!previewUrl) return;
                            setActiveImagePreview({ src: previewUrl, name: attachment.fileName });
                          }}
                        >
                          {imagePreviewUrls[attachment.id] ? (
                            <img
                              src={imagePreviewUrls[attachment.id]}
                              alt={attachment.fileName}
                              className="h-20 w-full object-cover"
                            />
                          ) : (
                            <div className="flex h-20 w-full items-center justify-center bg-slate-100 text-[10px] text-slate-500">
                              Loading
                            </div>
                          )}
                        </button>
                      ))}
                    </div>
                  ) : null}

                  <div className="space-y-2">
                    {fileAttachments.map((attachment) => (
                      <div
                        key={attachment.id}
                        className="group flex items-center justify-between gap-2 rounded-lg border border-slate-200 p-2"
                      >
                        <div className="min-w-0">
                          <p className="truncate text-xs font-medium text-slate-800">{attachment.fileName}</p>
                          <p className="text-[11px] text-slate-500">
                            {formatFileSize(attachment.fileSize)} - {new Date(attachment.created).toLocaleDateString()}
                          </p>
                        </div>
                        <button
                          type="button"
                          onClick={() => downloadAttachment(attachment.id, attachment.fileName)}
                          className="rounded border border-slate-300 px-2 py-1 text-[11px] text-slate-700 opacity-0 transition group-hover:opacity-100"
                        >
                          Download
                        </button>
                      </div>
                    ))}
                  </div>
                </>
              )}

              <form onSubmit={submitAttachments} className="space-y-2 border-t border-slate-100 pt-2">
                <label className="inline-flex cursor-pointer rounded-lg border border-slate-300 px-3 py-1.5 text-xs text-slate-700 hover:bg-slate-50">
                  Add attachments
                  <input
                    type="file"
                    multiple
                    className="hidden"
                    onChange={(e) => setFilesToUpload(Array.from(e.target.files ?? []))}
                  />
                </label>

                {filesToUpload.length > 0 ? (
                  <ul className="space-y-1 rounded-lg border border-slate-200 bg-slate-50 p-2 text-[11px] text-slate-600">
                    {filesToUpload.map((file) => (
                      <li key={`${file.name}-${file.lastModified}`}>
                        {file.name} - {formatFileSize(file.size)}
                      </li>
                    ))}
                  </ul>
                ) : null}

                <button
                  type="submit"
                  className="w-full rounded-lg bg-slate-900 px-3 py-2 text-xs font-semibold text-white hover:bg-slate-800 disabled:opacity-60"
                  disabled={filesToUpload.length === 0 || uploadAttachment.isLoading}
                >
                  {uploadAttachment.isLoading ? "Uploading..." : "Upload selected files"}
                </button>
                <p className="text-[11px] text-slate-500">Maximum file size: 100MB per attachment.</p>
              </form>
            </div>
          </Card>
        </div>
      </div>

      {activeImagePreview ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
          onClick={() => setActiveImagePreview(null)}
        >
          <div
            className="max-h-[90vh] max-w-[90vw] overflow-auto rounded-lg bg-white p-3"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-2 flex items-center justify-between gap-3">
              <p className="text-sm font-medium text-slate-800">{activeImagePreview.name}</p>
              <button
                type="button"
                className="rounded border border-slate-300 px-2 py-1 text-xs text-slate-700"
                onClick={() => setActiveImagePreview(null)}
              >
                Close
              </button>
            </div>
            <img
              src={activeImagePreview.src}
              alt={activeImagePreview.name}
              className="max-h-[75vh] max-w-[85vw] object-contain"
            />
            <div className="mt-3">
              <button
                type="button"
                onClick={() => {
                  const selectedAttachment = attachments.find(
                    (attachment) => imagePreviewUrls[attachment.id] === activeImagePreview.src
                  );
                  if (!selectedAttachment) return;
                  downloadAttachment(selectedAttachment.id, selectedAttachment.fileName);
                }}
                className="text-xs font-medium text-purple-700 hover:underline"
              >
                Download image
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
